// ============================================================================
// Hooks TanStack Query — ADMINISTRATION (AD-G) : onglet Rappels
// ============================================================================
// Hook = TanStack Query SEULEMENT (regle parc). L'autorisation vit au SERVEUR
// (P-ADM-2, routes/admin.ts : canAccessGestAdmin = admin SEUL — fonctions repo
// ADMIN-WIDE, jamais le grant par cible). Le bundle reminders est charge par
// onglet (P-ADM-8) sous ADMIN_ROOT_KEY. Les mutations invalident la famille
// admin (['gest','admin']).
//
// [!] PERIMETRE AD-G (immediat) : contenu editable + envoi (passe idempotente /
// renvoi / test) + kill-switch. Le recurrent (send_mode/send_day) et les envois
// programmes = item 9 (dispatcher absent) : ABSENTS. Historique/stats d'envoi =
// EmptyState (PDR-ADM-03, pas de table de log en C-native).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { ADMIN_ROOT_KEY, type GestAdminRole } from './useAdminAudit';

// ─── Types — MIROIR EXACT des DTOs serveur (routes/admin.ts AD-G) ─────────────

export type ReminderSurveyType = 'member_pulse' | 'team_pulse';
export const REMINDER_SURVEY_TYPES: readonly ReminderSurveyType[] = ['member_pulse', 'team_pulse'];

/** Contenu editable d'un courriel (isDefault = defaut en memoire, non sauve). */
export interface ReminderContent {
  surveyType: ReminderSurveyType;
  subject: string;
  heading: string;
  ctaLabel: string;
  intro: string;
  footnote: string;
  isDefault: boolean;
}

export interface ReminderTeamOption {
  id: string;
  name: string;
}

// ── Item 9b (DISPATCHER) — horaire recurrent + envois programmes ──────────────

export type ReminderSendMode = 'auto' | 'manual';
export const REMINDER_SEND_MODES: readonly ReminderSendMode[] = ['auto', 'manual'];

export interface ReminderSchedule {
  sendMode: ReminderSendMode;
  sendDayOfMonth: number | null;
}

export type ScheduledSendScope = 'all' | 'team';
export type ScheduledSendStatus = 'pending' | 'sent' | 'cancelled';

export interface ScheduledSend {
  id: string;
  scope: ScheduledSendScope;
  teamId: string | null;
  period: string;
  scheduledDate: string;
  status: ScheduledSendStatus;
  createdByEmail: string;
  sentAt: string | null;
  resultSummary: string | null;
}

export interface AdminRemindersResponse {
  viewer: { role: GestAdminRole; email: string };
  contents: Record<ReminderSurveyType, ReminderContent>;
  paused: boolean;
  teams: ReminderTeamOption[];
  schedule: ReminderSchedule;
  scheduled: ScheduledSend[];
}

export interface ReminderContentPayload {
  subject: string;
  heading: string;
  ctaLabel: string;
  intro: string;
  footnote: string;
}

/** Resultat de la passe mensuelle (Alert client). */
export interface MonthlyPassResult {
  period: string;
  recipients: number;
  sent: number;
  skipped: number;
  errors: number;
  throttled: number;
  alreadyRan?: boolean;
  paused?: boolean;
}

export interface ResendResult {
  period: string;
  teamId: string;
  recipients: number;
  sent: number;
  skipped: number;
  errors: number;
  throttled: number;
  paused?: boolean;
  noRecipient?: boolean;
}

export interface TestSendResult {
  recipients: number;
  sent: number;
  errors: number;
  throttled: number;
  paused?: boolean;
}

// ─── Codes d'erreur metier (pattern adminContractErrorCode, GA-21) ────────────

/**
 * Extrait le code metier renvoye par le serveur, sinon un code par statut.
 * Mappe sur gest:admin.reminders.errors.<code>. `not_configured` = 503 mailer
 * ACS non configure (capability-aware) ; les autres = generiques.
 */
export function adminReminderErrorCode(err: unknown): string {
  const body =
    err instanceof ApiServerError || err instanceof ApiForbiddenError ? err.originalBody : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  if (code === 'MAILER_NOT_CONFIGURED') return 'not_configured';
  if (code === 'MAILER_THROTTLED') return 'throttled';
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

// ─── Query (bundle par onglet, P-ADM-8) ───────────────────────────────────────

const adminRemindersKey = [...ADMIN_ROOT_KEY, 'reminders'] as const;

/** AD-G : bundle de l'onglet RAPPELS (contenus + kill-switch + equipes). */
export function useAdminReminders(enabled: boolean) {
  return useQuery({
    queryKey: adminRemindersKey,
    queryFn: () => api.get<AdminRemindersResponse>('/api/admin/reminders'),
    enabled,
    retry: false,
  });
}

// ─── Mutations (toutes invalident la famille admin) ───────────────────────────

function useInvalidateReminders() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_ROOT_KEY });
  };
}

/** Upsert d'un contenu de courriel (par survey_type). */
export function useUpsertReminderContent() {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: (input: { surveyType: ReminderSurveyType; payload: ReminderContentPayload }) =>
      api.put<{ ok: boolean }>(
        `/api/admin/reminders/contents/${encodeURIComponent(input.surveyType)}`,
        input.payload,
      ),
    onSuccess: () => invalidate(),
  });
}

/** Bascule du kill-switch global. */
export function useSetRemindersPaused() {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: (paused: boolean) =>
      api.post<{ ok: boolean; paused: boolean }>('/api/admin/reminders/pause', { paused }),
    onSuccess: () => invalidate(),
  });
}

/** Passe mensuelle (idempotente cote serveur). */
export function useRunMonthlyPass() {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: (input: { period?: string }) =>
      api.post<MonthlyPassResult>('/api/admin/reminders/run', input.period ? { period: input.period } : {}),
    onSuccess: () => invalidate(),
  });
}

/** Renvoi cible a une equipe. */
export function useResendReminder() {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: (input: { teamId: string; period: string }) =>
      api.post<ResendResult>('/api/admin/reminders/resend', input),
    onSuccess: () => invalidate(),
  });
}

/** Envoi test (hors JobRuns, hors audit). */
export function useSendReminderTest() {
  return useMutation({
    mutationFn: (input: { recipients: string[]; period: string; surveyType: ReminderSurveyType }) =>
      api.post<TestSendResult>('/api/admin/reminders/test', input),
  });
}

// ── Item 9b (DISPATCHER) — mutations horaire recurrent + envois programmes ─────

/** Horaire recurrent (send_mode + send_day_of_month). */
export function useSetReminderSchedule() {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: (input: { sendMode: ReminderSendMode; sendDayOfMonth: number }) =>
      api.put<{ ok: boolean; schedule: ReminderSchedule }>('/api/admin/reminders/schedule', input),
    onSuccess: () => invalidate(),
  });
}

/** Depose un envoi programme (pending). */
export function useCreateScheduledSend() {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: (input: { scope: ScheduledSendScope; teamId?: string; period: string; scheduledDate: string }) =>
      api.post<{ ok: boolean; id: string }>('/api/admin/reminders/scheduled', input),
    onSuccess: () => invalidate(),
  });
}

/** Annule un envoi programme pending. */
export function useCancelScheduledSend() {
  const invalidate = useInvalidateReminders();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean; id: string }>(`/api/admin/reminders/scheduled/${encodeURIComponent(id)}`),
    onSuccess: () => invalidate(),
  });
}
